import { stripStyleUrlFunction } from '@common/html/util/style';
import { EditorEvent } from '@portal-core/project-files/events/editor-event.event';
import { NodeViewBase } from '@portal-core/text-editor/node-views/node-view.base';
import { GetPosForNodeView } from '@portal-core/text-editor/node-views/types/nodeview-get-pos.type';
import { BidiBridge } from '@portal-core/util/bidi-bridge';
import { Node } from 'prosemirror-model';
import { Decoration, EditorView, NodeView } from 'prosemirror-view';

export class DropDownNodeView extends NodeViewBase implements NodeView {
  private onToggleDropdown: (event: CustomEvent) => void;

  /** Opt-in to sync the node when the stylesheet changes. */
  get syncNodeOnStylesheetChanges(): boolean {
    return true;
  }

  constructor(
    node: Node,
    editorView: EditorView,
    getPos: GetPosForNodeView,
    decorations: Decoration[],
    nodeViewBridge: BidiBridge<EditorEvent, EditorEvent>
  ) {
    super(node, editorView, getPos, decorations, nodeViewBridge);

    // Listen for toggle events from the dropdown head/hotspot node views
    this.onToggleDropdown = event => {
      // Stop propagation so a nested dropdown's toggle doesn't also toggle its ancestor dropdowns
      event.stopPropagation();
      this.dom.toggleAttribute('mc-lwe-drop-down-open');
      this.syncNode();
    };

    this.dom.addEventListener('toggle-drop-down', this.onToggleDropdown);

    // Create the contentDOM element and sync the node
    this.contentDOM = this.dom;
    this.syncNode();

    // Sync the node again once the dropdown hotspot is ready
    this.syncNodeWhenReady(() => !!this.contentDOM.querySelector<HTMLElement>('MadCap\\:dropDownHotspot'));
  }

  destroy() {
    this.dom.removeEventListener('toggle-drop-down', this.onToggleDropdown);
    super.destroy();
  }

  ignoreMutation(mutation: MutationRecord): boolean {
    // Ignore toggling the dropdown open attribute
    if (mutation.type === 'attributes' && mutation.target === this.dom && mutation.attributeName === 'mc-lwe-drop-down-open') {
      return true;
    }

    return super.ignoreMutation(mutation);
  }

  syncNode() {
    const isOpen = this.dom.hasAttribute('mc-lwe-drop-down-open');
    const dropDownHotspotDOM = this.contentDOM.querySelector<HTMLElement>('MadCap\\:dropDownHotspot');
    const dropDownBodyDOM = this.contentDOM.querySelector<HTMLElement>('MadCap\\:dropDownBody');

    // Update the body element
    if (dropDownBodyDOM) {
      dropDownBodyDOM.setAttribute('contenteditable', isOpen ? 'true' : 'false');
    }

    // Update the hotspot img and button elements
    if (dropDownHotspotDOM) {
      // Grab the mc styles for the dropdown image
      const closedImage = getComputedStyle(this.dom).getPropertyValue('--mc-closed-image') || 'url(assets/images/editor/flare/dropdownclosed.gif)';
      const openImage = getComputedStyle(this.dom).getPropertyValue('--mc-open-image') || 'url(assets/images/editor/flare/dropdownopen.gif)';
      const imageSpacing = getComputedStyle(this.dom).getPropertyValue('--mc-image-spacing') || '5px';
      const imagePosition = getComputedStyle(this.dom).getPropertyValue('--mc-image-position') || 'left';

      let imageSrc = isOpen ? stripStyleUrlFunction(openImage) : stripStyleUrlFunction(closedImage);
      if (imagePosition === 'none') {
        imageSrc = null;
      }

      // Grab the dom elements to update
      const buttonDOM = this.contentDOM.querySelector<HTMLButtonElement>('.mc-lwe-dropdownhotspot-button');
      const imgDOM = this.contentDOM.querySelector<HTMLImageElement>('.mc-lwe-dropdownhotspot-image');

      // Update the img and button elements
      if (imageSrc) {
        imgDOM.src = imageSrc;

        if (imagePosition === 'left') {
          buttonDOM.style.removeProperty('margin-left');
          buttonDOM.style.setProperty('margin-right', imageSpacing);
          // Ensure the button is the first child
          if (buttonDOM !== dropDownHotspotDOM.firstChild) {
            dropDownHotspotDOM.prepend(buttonDOM);
          }
        } else if (imagePosition === 'right') {
          buttonDOM.style.setProperty('margin-left', imageSpacing);
          buttonDOM.style.removeProperty('margin-right');
          // Ensure the button is the last child
          if (buttonDOM !== dropDownHotspotDOM.lastChild) {
            dropDownHotspotDOM.appendChild(buttonDOM);
          }
        }
      } else {
        imgDOM.removeAttribute('src');
        buttonDOM.style.removeProperty('margin-left');
        buttonDOM.style.removeProperty('margin-right');
      }
    }
    // There isn't a hotspot node, so update the drop down head button instead
    else {
      const dropDownHeadButtonDOM = this.contentDOM.querySelector<HTMLElement>('.mc-lwe-dropdownhead-button');
      dropDownHeadButtonDOM?.classList.toggle('mc-expansion-button-expanded', isOpen);
    }
  }
}
